import { catchError, of, switchMap, throttleTime } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { dispatch, RootState } from '../store';
import { EMPTY_ACTION } from './emptyAction';
import { Epic, ofType } from 'redux-observable';
import { getUserPreferences, putUserPreferences } from '../../apis/user-preferences';
import { LoadingStatus } from '../../types/profile';
import { shouldDataBeFetched } from '../../utils/profile';
import {
  JobsiteLocationPreference,
  UserPreferenceDataLoadPayload,
  UserPreferenceDataUpdatePayload,
} from '../../types/user-preferences';
import {
  loadJobsiteLocationPreference,
  setJobsiteLocationPreference,
  setJobsiteLocationPreferenceLoadingStatus,
  updateJobsiteLocationPreference,
} from '../slice/userPreferencesSlice';

const THROTTLE_TIME_FOR_USER_PREFERENCES_DATA_LOAD_EPICS = 500;

export const jobsiteLocationPreferenceLoadEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(loadJobsiteLocationPreference.type),
    throttleTime(THROTTLE_TIME_FOR_USER_PREFERENCES_DATA_LOAD_EPICS),
    switchMap(async (e) => {
      try {
        const state: RootState = cloneDeep(state$.value);
        const userPreferencesState = state.userPreferences;
        const payload: UserPreferenceDataLoadPayload = e.payload;

        const { refetch } = payload;
        const { jobsiteLocation } = userPreferencesState;

        if (!shouldDataBeFetched(jobsiteLocation.loadingStatus) && !refetch)
          return EMPTY_ACTION('Data already exists for jobsite location preferences');

        dispatch(setJobsiteLocationPreferenceLoadingStatus(LoadingStatus.IN_PROGRESS));

        try {
          const companyId = state.auth.authContext?.companyId;
          const userId = state.auth.authContext?.userId;
          if (!companyId || !userId) {
            throw new Error('error at companyDetailsLoadEpic, undefined companyId/userId');
          }

          const preferences = await getUserPreferences(companyId, userId);

          if (!preferences) {
            const stateUpdatePayload: JobsiteLocationPreference = {
              id: null,
            };
            dispatch(setJobsiteLocationPreference(stateUpdatePayload));
            return setJobsiteLocationPreferenceLoadingStatus(LoadingStatus.FAILED);
          }

          const stateUpdatePayload: JobsiteLocationPreference = {
            id: Number(preferences.jobsite_location_id),
          };
          dispatch(setJobsiteLocationPreference(stateUpdatePayload));
          return setJobsiteLocationPreferenceLoadingStatus(LoadingStatus.SUCCEEDED);
        } catch (error) {
          const stateUpdatePayload: JobsiteLocationPreference = {
            id: null,
          };
          dispatch(setJobsiteLocationPreference(stateUpdatePayload));
          return setJobsiteLocationPreferenceLoadingStatus(LoadingStatus.FAILED);
        }
      } catch (error) {
        const stateUpdatePayload: JobsiteLocationPreference = {
          id: null,
        };
        dispatch(setJobsiteLocationPreference(stateUpdatePayload));
        return EMPTY_ACTION(
          'error at jobsiteLocationPreferenceLoadEpic while fetching jobsite location preferences',
        );
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at jobsiteLocationPreferenceLoadEpic'));
    }),
  );

export const jobsiteLocationPreferenceUpdateEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(updateJobsiteLocationPreference.type),
    switchMap(async (e) => {
      try {
        const state: RootState = cloneDeep(state$.value);
        const authState = state.auth;
        const payload: UserPreferenceDataUpdatePayload = e.payload;

        const { locationId } = payload;
        dispatch(setJobsiteLocationPreferenceLoadingStatus(LoadingStatus.IN_PROGRESS));

        if (!authState.authContext) {
          throw new Error('error at jobsiteLocationPreferenceUpdateEpic, undefined authContext');
        }

        await putUserPreferences(authState.authContext.companyId, authState.authContext.userId, {
          jobsite_location_id: String(locationId),
        });
        return setJobsiteLocationPreference({ id: locationId });
      } catch (error) {
        const stateUpdatePayload: JobsiteLocationPreference = {
          id: null,
        };
        dispatch(setJobsiteLocationPreference(stateUpdatePayload));
        return EMPTY_ACTION(
          'error at jobsiteLocationPreferenceLoadEpic while fetching jobsite location preferences',
        );
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at jobsiteLocationPreferenceLoadEpic'));
    }),
  );
